/**
 * Sentry filtering utilities
 *
 * Centralizes logic for filtering out non-error events from Sentry:
 * - Auth telemetry (login/logout success/failure)
 * - Expected business-logic API errors (user not found, permission denied, etc.)
 * - Known browser race conditions (audio play/pause AbortError)
 */

import type { ErrorEvent } from '@sentry/react';

/**
 * Known business-logic error messages that should not be sent to Sentry.
 * These are expected API responses, not bugs.
 */
const EXPECTED_BUSINESS_ERRORS = [
  'user has already registered',
  'user does not exist',
  'no auth0 user id',
] as const;

/**
 * Check if an error message matches a known expected business error.
 * These errors are part of normal application flow and shouldn't pollute Sentry.
 *
 * @param message - Error message to check
 * @returns true if this is an expected business error
 */
export function isExpectedBusinessError(message: string | undefined): boolean {
  if (!message) {
    return false;
  }

  const lowerMessage = message.toLowerCase();
  return EXPECTED_BUSINESS_ERRORS.some((pattern) =>
    lowerMessage.includes(pattern),
  );
}

/**
 * URL patterns whose 404 responses are expected business outcomes (e.g. a user
 * has no role settings for a given form type, or a form type does not exist for
 * the current tenant). These are surfaced to the UI via PermissionGuard / detail
 * fallbacks and should not pollute Sentry.
 *
 * Scoped narrowly by status + path so non-404 errors and unrelated 404s on the
 * same backend (e.g. typos in URLs) are still captured.
 */
const EXPECTED_404_URL_PATTERNS: readonly RegExp[] = [
  // GET/PATCH /tenant/{slug}/users/{userId}/form-type-role-settings/{channelId}
  /\/tenant\/[^/]+\/users\/\d+\/form-type-role-settings\/\d+/,
  // GET /intake-channel/tenant/{slug}/{channelId}
  /\/intake-channel\/tenant\/[^/]+\/\d+(?:[/?]|$)/,
] as const;

/**
 * Check if a 404 response on the given URL is an expected business outcome.
 *
 * @param status - HTTP status code from the response
 * @param url - Request path (relative to the API base URL)
 * @returns true if this 404 should NOT be sent to Sentry
 */
export function isExpectedBusiness404(
  status: number | string | undefined,
  url: string | undefined,
): boolean {
  if (Number(status) !== 404 || !url) {
    return false;
  }
  return EXPECTED_404_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Determine if an error should be skipped from Sentry capture.
 * Use this before calling Sentry.captureException() to filter expected errors.
 *
 * @param error - Error object, string, or unknown value
 * @returns true if the error should NOT be sent to Sentry
 */
export function shouldSkipSentryCapture(error: unknown): boolean {
  if (!error) {
    return false;
  }

  // Handle Error objects
  if (error instanceof Error) {
    return isExpectedBusinessError(error.message);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return isExpectedBusinessError(error);
  }

  // Handle plain objects with message property
  if (typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') {
      return isExpectedBusinessError(message);
    }
  }

  return false;
}

/**
 * Sentry beforeSend hook to filter out events before they're sent.
 * Currently filters:
 * - Auth telemetry events (login/logout tracking)
 *
 * @param event - Sentry event to potentially filter
 * @returns event to send, or null to drop the event
 */
export function sentryBeforeSend(event: ErrorEvent): ErrorEvent | null {
  // Drop auth telemetry - these are info/warning logs, not errors
  if (event.tags?.event_type === 'auth_attempt') {
    return null;
  }

  return event;
}
